import {FC} from 'react';

import {AppBar, Box, Button, Toolbar, ToggleButton, ToggleButtonGroup, Typography} from '@mui/material';
import {matchPath, NavLink, useLocation} from "react-router-dom";
import {useAuth} from "hooks/useAuth";
import {useFilters} from "hooks/useFilter";
import {useAppParams} from "hooks/useAppParams";
import ROUTES from "routes";
import {capitalizeFirstLetterAndLowerRest, generateUrl} from 'helpers/global';
import {history} from "helpers/history";
import {EnvFilterType, RegionFilter} from "types/filterContext";
import {VIEWER_TABS} from "pages/NotificationViewer/Addons/definitions";

const ENVIRONMENTS: EnvFilterType[] = ['live', 'demo', 'dev', 'staging'];
const REGIONS: RegionFilter[] = ['us', 'eu'];

const AppHeader: FC = () => {
    const {logout} = useAuth();
    const {apiFilters, setApiFilters} = useFilters();
    const params = useAppParams();
    const location = useLocation();

    const environment = (params?.environment ?? apiFilters.environment) as EnvFilterType;
    const region = (params?.region ?? apiFilters.region) as RegionFilter;

    const navigateTo = (nextEnvironment: EnvFilterType, nextRegion: RegionFilter) => {
        setApiFilters({
            environment: nextEnvironment,
            region: nextRegion,
        });

        const currentTab = VIEWER_TABS.find(({route}) =>
            matchPath(location.pathname, {path: route.path, exact: true}),
        );

        const url = generateUrl((currentTab?.route ?? ROUTES.notificationViewerEmailLogs).path, {
            environment: nextEnvironment,
            region: nextRegion,
        });

        history.push(url);
    };

    return (
        <AppBar position="static" color="default" elevation={1}>
            <Toolbar variant="dense" sx={{gap: 3}}>
                <Typography
                    variant="h6"
                    component={NavLink}
                    to={generateUrl(ROUTES.notificationViewerEmailLogs.path, {region, environment})}
                    sx={{color: 'inherit', textDecoration: 'none', flexShrink: 0}}
                >
                    Notification Viewer
                </Typography>

                <Box sx={{display: 'flex', alignItems: 'center', gap: 3, marginLeft: 'auto'}}>
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={environment}
                        onChange={(_, next) => next && navigateTo(next, region)}
                        aria-label="Environment"
                    >
                        {ENVIRONMENTS.map((item) => (
                            <ToggleButton key={item} value={item} data-testid={`env-${item}`}>
                                {capitalizeFirstLetterAndLowerRest(item)}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>

                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={region}
                        onChange={(_, next) => next && navigateTo(environment, next)}
                        aria-label="Region"
                    >
                        {REGIONS.map((item) => (
                            <ToggleButton key={item} value={item} data-testid={`region-${item}`}>
                                {item.toUpperCase()}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>

                    <Button variant="outlined" color="inherit" data-testid="app-header--sign-out"
                            onClick={() => logout()}>
                        Log out
                    </Button>
                </Box>
            </Toolbar>
        </AppBar>
    );
};

export default AppHeader;
